import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  ListItemIcon,
  IconButton,
  makeStyles,
  Icon,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@material-ui/core";
import { Button, Card, Grid, Typography, Menu, MenuItem, Tooltip } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { GetApp, MoreVert, MailOutline, DeleteOutline, Add, CheckBox } from "@material-ui/icons";
import ActionsDialog from "features/common/components/ActionsDialog";
import Edit from "assets/icons/file-checklist-edit.svg";
import { emptyArray, emptyString } from "constants/defaultValues";
import productPageStyle from "features/products/styles";
import { getTenant } from "providers/TenantProvider";
import { countryCodeConstants } from "features/common/constants";
import { useApolloClient } from "@apollo/client";
import { useMutationWithErrorHandling } from "hooks/errorHandling";
import { CLONE_PRODUCT, SET_SELECTED_PRODUCT } from "../mutations";
import EditProductContainer from "./EditProductContainer";
import { get, set, useChangeTrackingLens } from "@totalsoft/change-tracking-react";
import * as V from "@totalsoft/pure-validations";
import EmailAddressContent from "features/common/components/EmailAddressContent";
import { rvCalculationCodeConstants } from "../constants";

const useStyles = makeStyles(productPageStyle);

function DataModifiedWarning(t, openWarning, handleWarningActions, handleCloseWarning) {
  return (
    <Dialog
      id="warning_dialog"
      open={(openWarning |> get) === "FIRST" || "SECOND"}
      onClose={handleCloseWarning}
      aria-labelledby="modified_warning_title"
      aria-describedby="modified_warning_description"
    >
      <DialogTitle id="modified_warning_title">{t("Dialog.Caution")}</DialogTitle>
      <DialogContent dividers>
        <DialogContentText id="modified_warning_description">
          {(openWarning |> get) === "FIRST" && t("Product.SomeParametersHaveBeenManuallyModified")}
          {(openWarning |> get) === "SECOND" && t("Product.WantToDiscardModificationsAndEditParam")}
        </DialogContentText>
        <DialogActions>
          <Button id="no_warning" color="secondary" onClick={handleWarningActions}>
            {t("Product.KeepModifications")}
          </Button>
          <Button id="yes_warning" color="primary" autoFocus onClick={handleWarningActions}>
            {t("Product.YesEditParameters")}
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}

function Product({
  product,
  onRemoveProduct,
  currencyCode,
  showAmountsWithVAT,
  boolFinanceProductsDisabled,
  onDownloadProducts,
  onSendProductsOnEmail,
}) {
  const [openEditProductLens] = useChangeTrackingLens();

  const [openWarning] = useChangeTrackingLens();

  const { t } = useTranslation();
  const styleProps = useMemo(() => ({ selected: product.selected }), [product.selected]);
  const classes = useStyles(styleProps);
  const client = useApolloClient();
  const { countryCode } = getTenant();
  const svgIcon = (
    <Icon className={classes.iconStyle}>
      <img src={Edit} alt="React Logo" />
    </Icon>
  );
  const [anchorEl, setAnchorEl] = useState(null);

  const [actionsDialogProps, setActionsDialogProps] = useState({
    open: false,
    title: emptyString,
    message: emptyString,
    actions: emptyArray,
  });

  const downloadButtonReadOnly = useMemo(() => {
    return (
      (product?.priceCard?.rvCalculationCode === rvCalculationCodeConstants.AOLRV &&
        product?.residualValueAmount === 0 &&
        product.hasSchedule) ||
      !product.hasSchedule
    );
  }, [product]);

  const [cloneProduct] = useMutationWithErrorHandling(CLONE_PRODUCT, {
    variables: { quoteProductId: product.quoteProductId },
    onCompleted: () => {
      const quoteCacheId = client.cache.identify({ __typename: "Quote", quoteId: product.quoteId });
      client.cache.evict({ id: quoteCacheId, fieldName: "quoteProducts" });
      client.cache.gc();
    },
  });

  const handleActions = useCallback(
    (event) => {
      const open = openWarning |> get;

      switch (event.currentTarget.id) {
        case "simple-menu":
          break;
        case "view_options":
          setAnchorEl(event.currentTarget);
          return;
        case "delete_product":
          setActionsDialogProps({
            open: true,
            title: t("General.Caution"),
            message: t("General.ConfirmDeleteItem"),
            actions: [
              {
                caption: t("General.ConfirmDeleteItemYes"),
                handle: () => {
                  onRemoveProduct(product.quoteProductId);
                  return true;
                },
              },
              {
                caption: t("General.ConfirmDeleteItemNo"),
                color: "secondary",
                handle: () => {
                  return true;
                },
              },
            ],
          });
          break;
        case "edit_product":
          if (!product.containsBackOfficeChanges) (openEditProductLens |> set)("BASIC");
          else (openWarning |> set)("FIRST");
          break;
        case "download_product":
          onDownloadProducts([product.quoteProductId]);
          break;
        case "send_via_email":
          setActionsDialogProps({
            open: true,
            title: t("Product.ProvideUsEmailAddress"),
            validator: V.shape({ email: [V.required, V.email] |> V.stopOnFirstFailure }),
            actions: [
              {
                caption: t("General.Confirm"),
                handle: (state, validate) => {
                  if (!validate(state)) return;
                  onSendProductsOnEmail(state.email, [product.quoteProductId]);
                  return true;
                },
              },
            ],
            Content: EmailAddressContent,
          });
          break;
        case "add_alternative_quote":
          cloneProduct();
          break;
        case "yes_warning":
          if (open === "FIRST") {
            (openWarning |> set)("SECOND");
            return;
          }
          if (open === "SECOND") {
            (openEditProductLens |> set)("BASIC");
            (openWarning |> set)();
            return;
          }
          break;
        case "no_warning":
          if (open === "FIRST" || open === "SECOND") {
            (openWarning |> set)();
            return;
          }
          break;
        case "warning_dialog":
          (openWarning |> set)();
          break;
      }

      setAnchorEl(null);
    },
    [
      t,
      product,
      onRemoveProduct,
      openEditProductLens,
      onDownloadProducts,
      onSendProductsOnEmail,
      cloneProduct,
      openWarning,
    ]
  );

  const handleCloseWarning = useCallback(() => {
    (openWarning |> set)();
  }, [openWarning]);

  const [setSelectedProduct, { loading: savingSelection }] = useMutationWithErrorHandling(SET_SELECTED_PRODUCT, {
    variables: { quoteId: product.quoteId, quoteProductId: product.quoteProductId },
    onCompleted: () => {
      const quoteProductCacheId = client.cache.identify({
        __typename: "QuoteProduct",
        quoteProductId: product.quoteProductId,
      });

      client.cache.evict({ id: quoteProductCacheId, fieldName: "selected" });
      client.cache.gc();
    },
  });

  const handleSelect = useCallback(() => {
    setSelectedProduct();
  }, [setSelectedProduct]);

  return (
    <Card id={"productCard" + product.quoteProductId} className={classes.card}>
      <Grid container>
        <Grid item xs={12} lg={2} className={classes.productSection}>
          <div className={classes.arrowTipItem}>
            <Tooltip title={product.translatedProductDescription || emptyString}>
              <Typography variant="body2" className={classes.productName}>
                {product.productName}
              </Typography>
            </Tooltip>
            {product.scheduleIsConfigurable &&
              countryCode !== countryCodeConstants.china &&
              countryCode !== countryCodeConstants.romania && (
                <Typography variant="body2" className={classes.scheduleType}>
                  {t("Product.SeasonalSchedule")}
                </Typography>
              )}
            {!product.scheduleIsConfigurable && countryCode === countryCodeConstants.brazil && (
              <Typography variant="body2" className={classes.scheduleType}>
                {t("Product.InstallmentAmount")}
              </Typography>
            )}
          </div>
        </Grid>
        <Grid item xs={12} lg={10} container className={classes.quoteInfo}>
          <Grid
            item
            container
            xs={4}
            spacing={2}
            justifyContent="space-around"
            alignItems="center"
            className={classes.assetSection}
          >
            <Grid item xs={6} container spacing={2} direction="column" justifyContent="space-around">
              <Grid item>
                <Typography className={classes.assetLabel9}>
                  {showAmountsWithVAT ? t("Product.DownPaymentWithVAT") : t("Product.DownPayment")}
                </Typography>
                <Typography className={classes.reducedFont}>
                  {currencyCode +
                    " " +
                    t("DECIMAL2", {
                      number: showAmountsWithVAT ? product.downPaymentAmountWithVAT : product.downPaymentAmount,
                    })}
                </Typography>
              </Grid>
              <Grid item>
                <Typography className={classes.assetLabel9}>{t("Product.Term")}</Typography>
                <Typography className={classes.reducedFont}>{product.duration}</Typography>
              </Grid>
            </Grid>

            <Grid item xs={6}>
              {product.residualValueVisible && (
                <>
                  <Typography className={classes.assetLabel9}>
                    {showAmountsWithVAT ? t("Product.ResidualValueWithVAT") : t("Product.ResidualValue")}
                  </Typography>
                  <Typography className={classes.reducedFont}>
                    {currencyCode}
                    {t("DECIMAL2", {
                      number: showAmountsWithVAT ? product.residualValueAmountWithVAT : product.residualValueAmount,
                    })}
                  </Typography>
                </>
              )}
            </Grid>
          </Grid>
          <Grid item container xs={4} justifyContent="flex-start" alignItems="center">
            <Button
              id="edit_product"
              className={classes.buttonStyle}
              disabled={boolFinanceProductsDisabled}
              onClick={handleActions}
            >
              {svgIcon}
              {t("General.Edit")}
            </Button>
          </Grid>
          <Grid item container xs={1} justifyContent="flex-end" alignItems="center">
            {product.containsBackOfficeChanges && (
              <Tooltip
                title={
                  <>
                    <p>{t("Product.ModifiedInfo")}</p>
                    <p>{t("Product.DiscardModifications")}</p>
                  </>
                }
                placement="top"
              >
                <Typography variant="caption" className={classes.modifiedInfo}>
                  {t("Product.Modified")}
                </Typography>
              </Tooltip>
            )}
          </Grid>
          {(openEditProductLens |> get) && (
            <EditProductContainer openEditProductLens={openEditProductLens} quoteProductId={product.quoteProductId} />
          )}
          {((openWarning |> get) === "FIRST" || (openWarning |> get) === "SECOND") &&
            DataModifiedWarning(t, openWarning, handleActions, handleCloseWarning)}
          <Grid item container xs={3} justifyContent="space-between">
            <Grid item xs container direction="column" justifyContent="space-around" alignItems="center">
              {savingSelection && <CircularProgress size={20} />}
              {product.selected && !savingSelection && (
                <IconButton className={classes.checkBox}>{<CheckBox />}</IconButton>
              )}
              {!savingSelection && !product.selected && (
                <Button
                  variant="outlined"
                  className={classes.reducedFont}
                  onClick={handleSelect}
                  disabled={boolFinanceProductsDisabled || !product.hasSchedule}
                >
                  {t("General.Select")}
                </Button>
              )}
            </Grid>
            <Grid item>
              <Tooltip title={t("General.MoreActions")}>
                <IconButton id="view_options" onClick={handleActions} size="small">
                  <MoreVert />
                </IconButton>
              </Tooltip>
              <Menu id="simple-menu" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleActions}>
                <MenuItem
                  id="delete_product"
                  onClick={handleActions}
                  disabled={boolFinanceProductsDisabled || product.selected}
                >
                  <ListItemIcon>
                    <DeleteOutline fontSize="small" />
                  </ListItemIcon>
                  <Typography variant="inherit">{t("General.DeleteItem")}</Typography>
                </MenuItem>
                <MenuItem id="download_product" onClick={handleActions} disabled={downloadButtonReadOnly}>
                  <ListItemIcon>
                    <GetApp fontSize="small" />
                  </ListItemIcon>
                  <Typography variant="inherit">{t("Product.Download")}</Typography>
                </MenuItem>
                <MenuItem id="send_via_email" onClick={handleActions} disabled={!product.hasSchedule}>
                  <ListItemIcon>
                    <MailOutline fontSize="small" />
                  </ListItemIcon>
                  <Typography variant="inherit">{t("Product.SendViaEMAIL")}</Typography>
                </MenuItem>
                <MenuItem id="add_alternative_quote" onClick={handleActions} disabled={boolFinanceProductsDisabled}>
                  <ListItemIcon>
                    <Add fontSize="small" />
                  </ListItemIcon>
                  <Typography variant="inherit">{t("Product.AddAlternativeQuote")}</Typography>
                </MenuItem>
              </Menu>
              <ActionsDialog
                open={actionsDialogProps.open}
                handleClose={useCallback(() => setActionsDialogProps((prev) => ({ ...prev, open: false })), [])}
                title={actionsDialogProps.title}
                message={actionsDialogProps.message}
                validator={actionsDialogProps.validator}
                actions={actionsDialogProps.actions}
                Content={actionsDialogProps.Content}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
}

Product.propTypes = {
  product: PropTypes.object.isRequired,
  onRemoveProduct: PropTypes.func.isRequired,
  onDownloadProducts: PropTypes.func.isRequired,
  onSendProductsOnEmail: PropTypes.func.isRequired,
  currencyCode: PropTypes.string.isRequired,
  showAmountsWithVAT: PropTypes.bool.isRequired,
  boolFinanceProductsDisabled: PropTypes.bool.isRequired,
};

export default Product;
